import {
  calculateLoanSummary,
  calculateLoanAmountFromEMI,
  getAverageIncome,
  getTotalObligations,
  getAllowedFoirPercentage,
  getEligibilityStatus,
  getIncomeBreakdownData,
  getObligationBreakdownData,
  getFoirBreakdownData,
  getEligibilityComparisonData,
  roundCurrency,
  parseNumber,
  formatCurrency,
  formatAxisCurrency,
} from "@/utils/finance";

import {
  Calculator,
  Wallet,
  Percent,
  CreditCard,
  Landmark,
  BadgeCheck,
  BadgePercent,
} from "lucide-react";

import InsightCard from "@/components/calculator/InsightCard";

import DataTable from "@/components/calculator/DataTable";

import {
  ACTIONS,
  CHARTS,
  FIELD_TYPES,
  RESULT_TYPES,
} from "@/utils/calculatorSchema";

const detailedFoirCalculatorConfig = {
  id: "detailed-foir",

  shortTitle: "FOIR",

  title: "Detailed FOIR Calculator",

  subtitle:
    "Evaluate repayment capacity using lender FOIR norms for Salaried, SEP and SENP applicants.",

  description:
    "Calculates average income, proposed EMI, FOIR percentage and lending eligibility for Home Loans and Plot Loans.",

  icon: BadgePercent,

  keywords: [
    "FOIR",
    "Loan Eligibility",
    "Income",
    "Repayment Capacity",
    "Home Loan",
    "Plot Loan",
  ],

  fields: [
    /* -------------------------------------------------- */
    /* Applicant */
    /* -------------------------------------------------- */

    {
      type: FIELD_TYPES.SECTION,
      title: "Applicant Details",
    },

    {
      key: "category",

      label: "Applicant Category",

      type: FIELD_TYPES.SELECT,

      defaultValue: "salaried",

      options: [
        { label: "Salaried", value: "salaried" },
        { label: "SEP", value: "sep" },
        { label: "SENP", value: "senp" },
      ],
    },

    {
      key: "loanType",

      label: "Loan Type",

      type: FIELD_TYPES.SELECT,

      defaultValue: "home",

      options: [
        { label: "Home Loan", value: "home" },
        {
          label: "Plot Loan for Construction",
          value: "plot",
        },
      ],
    },

    {
      key: "location",

      label: "Location",

      type: FIELD_TYPES.SELECT,

      defaultValue: "metro",

      visible: ({ values }) =>
        values.loanType === "plot" && values.category === "salaried",

      options: [
        {
          label: "Metro",
          value: "metro",
        },
        {
          label: "Non-Metro",
          value: "nonmetro",
        },
      ],
    },

    /* -------------------------------------------------- */
    /* Proposed Loan */
    /* -------------------------------------------------- */

    {
      type: FIELD_TYPES.SECTION,
      title: "Proposed Loan",
    },

    {
      key: "loanAmount",

      label: "Loan Amount",

      type: FIELD_TYPES.CURRENCY,

      defaultValue: 5000000,

      min: 100000,

      max: 500000000,

      step: 50000,
    },

    {
      key: "annualRate",

      label: "Interest Rate",

      type: FIELD_TYPES.PERCENT,

      defaultValue: 8.5,

      min: 5,

      max: 18,

      step: 0.05,
    },

    {
      key: "tenureYears",

      label: "Loan Tenure",

      type: FIELD_TYPES.NUMBER,

      suffix: "Years",

      defaultValue: 20,

      min: 1,

      max: 35,
    },

    /* -------------------------------------------------- */
    /* Salaried Income */
    /* -------------------------------------------------- */

    {
      type: FIELD_TYPES.SECTION,

      title: "Net Monthly Income (Last 3 Months)",

      visible: ({ values }) => values.category === "salaried",
    },

    {
      key: "month1",

      label: "Month 1",

      type: FIELD_TYPES.CURRENCY,

      defaultValue: 55000,

      step: 10000,

      min: 0,

      max: 10000000,

      visible: ({ values }) => values.category === "salaried",
    },

    {
      key: "month2",

      label: "Month 2",

      type: FIELD_TYPES.CURRENCY,

      defaultValue: 55000,

      step: 10000,

      min: 0,

      max: 10000000,

      visible: ({ values }) => values.category === "salaried",
    },

    {
      key: "month3",

      label: "Month 3",

      type: FIELD_TYPES.CURRENCY,

      defaultValue: 60000,

      step: 10000,

      min: 0,

      max: 10000000,

      visible: ({ values }) => values.category === "salaried",
    },

    /* -------------------------------------------------- */
    /* SEP/SENP */
    /* -------------------------------------------------- */

    {
      type: FIELD_TYPES.SECTION,

      title: "Average Annual Income (Last 2 Financial Years)",

      visible: ({ values }) => values.category !== "salaried",
    },

    {
      key: "fy1",

      label: "Financial Year 1",

      type: FIELD_TYPES.CURRENCY,

      defaultValue: 900000,

      visible: ({ values }) => values.category !== "salaried",
    },

    {
      key: "fy2",

      label: "Financial Year 2",

      type: FIELD_TYPES.CURRENCY,

      defaultValue: 960000,

      visible: ({ values }) => values.category !== "salaried",
    },

    /* -------------------------------------------------- */
    /* Obligations */
    /* -------------------------------------------------- */

    {
      type: FIELD_TYPES.SECTION,
      title: "Existing Obligations",
    },

    {
      key: "obligations",

      label: "Existing Obligations",

      type: FIELD_TYPES.ARRAY,

      defaultValue: [],

      addButtonLabel: "Add Obligation",

      itemFields: [
        {
          key: "type",
          label: "Obligation",
          type: FIELD_TYPES.SELECT,
          defaultValue: "car",
          options: [
            { label: "Car Loan", value: "car" },
            { label: "Personal Loan", value: "personal" },
            { label: "Credit Card", value: "credit-card" },
            { label: "Education Loan", value: "education" },
            { label: "Other", value: "other" },
          ],
        },

        {
          key: "amount",
          label: "Monthly EMI",
          type: FIELD_TYPES.CURRENCY,
          defaultValue: 0,
          min: 0,
          step: 500,
        },
      ],
    },
  ],

  summaryCards: [
    {
      title: "Average Monthly Income",
      key: "averageMonthlyIncome",
      type: RESULT_TYPES.CURRENCY,
      description: "Average monthly income considered for FOIR calculation.",
      icon: Wallet,
    },

    {
      title: "Allowed FOIR",
      key: "allowedFoir",
      displayKey: "allowedFoir",
      suffix: "%",
      type: RESULT_TYPES.PERCENT,
      description: "Maximum FOIR permitted according to lender policy.",
      icon: Percent,
    },

    {
      title: "Eligible EMI",
      key: "eligibleEmi",
      type: RESULT_TYPES.CURRENCY,
      description: "Maximum EMI available after existing obligations.",
      icon: CreditCard,
    },

    {
      title: "Proposed EMI",
      key: "proposedEmi",
      type: RESULT_TYPES.CURRENCY,
      description: "EMI of the proposed loan.",
      icon: Landmark,
    },

    {
      title: "Maximum Loan Eligibility",
      key: "maximumEligibleLoan",
      type: RESULT_TYPES.CURRENCY,
      description: "Highest loan amount supported under FOIR norms.",
      icon: Calculator,
    },

    {
      title: "FOIR Status",
      key: "eligibilityStatus",
      type: RESULT_TYPES.TEXT,
      description: "Overall lending eligibility based on FOIR.",
      icon: BadgeCheck,
    },
  ],

  charts: [
    {
      id: "income-breakdown",

      title: "Income Allocation",

      description:
        "Breakdown of monthly income into obligations, EMI capacity and unused income.",

      type: CHARTS.PIE,

      resultsKey: "incomeBreakdown",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,
    },

    {
      id: "obligation-breakdown",

      title: "Existing Obligations",

      description: "Distribution of all existing monthly loan obligations.",

      type: CHARTS.PIE,

      resultsKey: "obligationBreakdown",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,
    },

    {
      id: "foir-breakdown",

      title: "Monthly Income Utilisation",

      description:
        "Shows how monthly income is distributed between existing EMIs, proposed EMI and remaining income.",

      type: CHARTS.PIE,

      resultsKey: "foirBreakdown",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,
    },

    {
      id: "emi-comparison",

      title: "Eligible vs Proposed EMI",

      description:
        "Compare lender-supported EMI capacity with the proposed loan EMI.",

      type: CHARTS.BAR,

      resultsKey: "emiComparison",

      minWidth: 550,

      xAxis: {
        dataKey: "label",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      bars: [
        {
          dataKey: "amount",
          name: "Monthly EMI",
        },
      ],
    },
  ],

  tables: [
    {
      title: "Existing Obligations",

      component: DataTable,

      dataKey: "obligations",

      columns: [
        {
          key: "srNo",
          label: "Sr.",
          type: "number",
        },
        {
          key: "type",
          label: "Obligation",
        },
        {
          key: "amount",
          label: "Monthly EMI",
          type: "currency",
        },
      ],
    },
  ],

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const {
      category,
      loanType,
      location,
      loanAmount,
      annualRate,
      tenureYears,
      obligations = [],
    } = values;

    /* -------------------------------------------------- */
    /* Average Income                                     */
    /* -------------------------------------------------- */

    const { averageMonthlyIncome, averageAnnualIncome } =
      getAverageIncome(values);

    /* -------------------------------------------------- */
    /* Existing Obligations                               */
    /* -------------------------------------------------- */

    const totalObligations = getTotalObligations(obligations);

    /* -------------------------------------------------- */
    /* Allowed FOIR                                       */
    /* -------------------------------------------------- */

    const allowedFoir = getAllowedFoirPercentage({
      category,
      loanType,
      location,
      averageMonthlyIncome,
    });

    /* -------------------------------------------------- */
    /* EMI Capacity                                       */
    /* -------------------------------------------------- */

    const maximumEmi = roundCurrency(
      averageMonthlyIncome * (allowedFoir / 100),
    );

    const eligibleEmi = roundCurrency(
      Math.max(maximumEmi - totalObligations, 0),
    );

    /* -------------------------------------------------- */
    /* Proposed Loan                                      */
    /* -------------------------------------------------- */

    const loanSummary = calculateLoanSummary({
      principal: loanAmount,
      annualRate,
      tenureYears,
    });

    const proposedEmi = loanSummary.emi;

    const actualFoir =
      averageMonthlyIncome === 0
        ? 0
        : roundCurrency(
            ((proposedEmi + totalObligations) / averageMonthlyIncome) * 100,
          );

    /* -------------------------------------------------- */
    /* Eligibility                                        */
    /* -------------------------------------------------- */

    const eligibility = getEligibilityStatus({
      category,
      averageMonthlyIncome,
      averageAnnualIncome,
      proposedEmi,
      eligibleEmi,
    });

    const {
      status,
      approved,
      minimumEligible,
      minimumReason,
      headroom,
      shortfall,
    } = eligibility;

    /* -------------------------------------------------- */
    /* Recommendation                                     */
    /* -------------------------------------------------- */

    let recommendation;

    switch (status) {
      case "NOT_ELIGIBLE":
        recommendation = {
          title: "Minimum Income Criteria Not Met",
          variant: "destructive",
          description:
            minimumReason || "Minimum income criteria are not satisfied.",
        };
        break;

      case "EXCEEDS_FOIR":
        recommendation = {
          title: "FOIR Limit Exceeded",
          variant: "warning",
          description:
            "The proposed EMI exceeds the lender's permitted FOIR. Consider reducing the loan amount, extending the tenure, or repaying existing obligations.",
        };
        break;

      default:
        recommendation = {
          title:
            headroom >= proposedEmi * 0.25
              ? "Excellent FOIR Position"
              : "FOIR Cleared",
          variant: "success",
          description:
            headroom >= proposedEmi * 0.25
              ? "The proposed loan comfortably satisfies the lender's FOIR policy with healthy repayment capacity."
              : "The proposed loan satisfies the lender's FOIR requirement, although the available EMI headroom is limited.",
        };
    }

    /* -------------------------------------------------- */
    /* Maximum Eligible Loan                              */
    /* -------------------------------------------------- */

    const maximumEligibleLoan = minimumEligible
      ? calculateLoanAmountFromEMI(eligibleEmi, annualRate, tenureYears)
      : 0;

    /* -------------------------------------------------- */
    /* Summary                                            */
    /* -------------------------------------------------- */

    const summary = {
      category,
      loanType,
      location,

      averageMonthlyIncome,
      averageAnnualIncome,

      totalObligations,

      allowedFoir,

      maximumEmi,
      eligibleEmi,
      proposedEmi,

      actualFoir,
      incomeUtilisation: actualFoir,

      headroom,
      shortfall,

      maximumEligibleLoan,

      minimumEligible,
      minimumEligibilityReason: minimumReason,

      approval: approved,

      eligibilityStatus: status,

      recommendation,

      loanAmount,
      annualRate,
      tenureYears,

      headroomText:
        headroom >= 0
          ? `Headroom of ₹${headroom.toLocaleString("en-IN")}`
          : `Shortfall of ₹${shortfall.toLocaleString("en-IN")}`,
    };

    /* -------------------------------------------------- */
    /* Charts                                             */
    /* -------------------------------------------------- */

    const charts = {
      incomeBreakdown: getIncomeBreakdownData({
        averageMonthlyIncome,
        totalObligations,
        eligibleEmi,
      }),

      obligationBreakdown: getObligationBreakdownData(obligations),

      foirBreakdown: getFoirBreakdownData({
        averageMonthlyIncome,
        proposedEmi,
        totalObligations,
      }),

      emiComparison: getEligibilityComparisonData({
        eligibleEmi,
        proposedEmi,
      }),
    };

    /* -------------------------------------------------- */
    /* Tables                                             */
    /* -------------------------------------------------- */

    const tables = {
      obligations: obligations.map((item, index) => ({
        srNo: index + 1,
        type: item.type,
        amount: parseNumber(item.amount),
      })),
    };

    /* -------------------------------------------------- */
    /* Return                                             */
    /* -------------------------------------------------- */

    return {
      summary,
      charts,
      tables,

      metadata: {
        generatedAt: new Date().toISOString(),

        category,
        loanType,
        location,

        annualRate,
        tenureYears,
      },
    };
  },

  exportExcel: ({ results }) => ({
    "Existing Obligations": results.tables.obligations ?? [],
  }),

  renderInsights: ({ results }) => {
    const { summary } = results;

    const {
      recommendation,
      category,
      loanType,
      averageMonthlyIncome,
      averageAnnualIncome,
      allowedFoir,
      maximumEmi,
      eligibleEmi,
      proposedEmi,
      actualFoir,
      totalObligations,
      maximumEligibleLoan,
      minimumEligibilityReason,
      eligibilityStatus,
      headroom,
      shortfall,
    } = summary;

    return (
      <>
        {recommendation && (
          <InsightCard
            variant={recommendation.variant}
            title={recommendation.title}
          >
            <p>{recommendation.description}</p>
          </InsightCard>
        )}

        <InsightCard title="Income & FOIR Assessment" variant="info">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Applicant Category:
              <strong className="ml-1">
                {category === "salaried"
                  ? "Salaried"
                  : category === "sep"
                    ? "SEP"
                    : "SENP"}
              </strong>
            </li>

            <li>
              Loan Type:
              <strong className="ml-1">
                {loanType === "home" ? "Home Loan" : "Plot Loan"}
              </strong>
            </li>

            <li>
              Average Monthly Income:
              <strong className="ml-1">
                {formatCurrency(averageMonthlyIncome)}
              </strong>
            </li>

            <li>
              Average Annual Income:
              <strong className="ml-1">
                {formatCurrency(averageAnnualIncome)}
              </strong>
            </li>

            <li>
              Allowed FOIR:
              <strong className="ml-1">{allowedFoir}%</strong>
            </li>

            <li>
              Maximum EMI permitted under FOIR:
              <strong className="ml-1">{formatCurrency(maximumEmi)}</strong>
            </li>

            <li>
              Existing monthly obligations:
              <strong className="ml-1">
                {formatCurrency(totalObligations)}
              </strong>
            </li>

            <li>
              Eligible EMI after obligations:
              <strong className="ml-1">{formatCurrency(eligibleEmi)}</strong>
            </li>
          </ul>
        </InsightCard>

        <InsightCard
          title="Proposed Loan Evaluation"
          variant={
            eligibilityStatus === "FOIR_CLEARED"
              ? "success"
              : eligibilityStatus === "EXCEEDS_FOIR"
                ? "warning"
                : "destructive"
          }
        >
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Proposed EMI:
              <strong className="ml-1">{formatCurrency(proposedEmi)}</strong>
            </li>

            <li>
              Actual FOIR:
              <strong className="ml-1">{actualFoir}%</strong>
            </li>

            {eligibilityStatus === "NOT_ELIGIBLE" ? (
              <>
                <li>Minimum eligibility requirement not satisfied.</li>

                <li>
                  <strong>{minimumEligibilityReason}</strong>
                </li>

                <li>
                  Loan eligibility cannot be determined until the minimum income
                  criteria are met.
                </li>
              </>
            ) : eligibilityStatus === "FOIR_CLEARED" ? (
              <>
                <li>
                  Your proposed EMI falls within the lender's permitted FOIR.
                </li>

                <li>
                  Remaining EMI capacity:
                  <strong className="ml-1">{formatCurrency(headroom)}</strong>
                </li>
              </>
            ) : (
              <>
                <li>The proposed EMI exceeds the lender's permitted FOIR.</li>

                <li>
                  EMI reduction required:
                  <strong className="ml-1">{formatCurrency(shortfall)}</strong>
                </li>
              </>
            )}
          </ul>
        </InsightCard>

        <InsightCard title="Maximum Loan Eligibility" variant="success">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Based on your income and current obligations, the estimated
              maximum eligible loan amount is
              <strong className="ml-1">
                {formatCurrency(maximumEligibleLoan)}
              </strong>
              .
            </li>

            <li>
              Eligibility calculations assume the selected interest rate and
              tenure remain unchanged.
            </li>

            <li>
              Actual sanction may vary depending on lender policies, credit
              score, property valuation and supporting documents.
            </li>
          </ul>
        </InsightCard>

        <InsightCard
          title="Recommendations"
          variant={
            eligibilityStatus === "FOIR_CLEARED"
              ? "default"
              : eligibilityStatus === "EXCEEDS_FOIR"
                ? "warning"
                : "destructive"
          }
        >
          {eligibilityStatus === "NOT_ELIGIBLE" ? (
            <ul className="list-disc space-y-1 pl-5">
              <li>Meet the minimum income requirement before applying.</li>

              <li>
                <strong>{minimumEligibilityReason}</strong>
              </li>

              <li>Recalculate eligibility after your income increases.</li>
            </ul>
          ) : eligibilityStatus === "EXCEEDS_FOIR" ? (
            <ul className="list-disc space-y-1 pl-5">
              <li>Reduce the proposed loan amount.</li>

              <li>Increase the repayment tenure.</li>

              <li>Repay existing loans to improve FOIR.</li>

              <li>Add a co-applicant if permitted.</li>
            </ul>
          ) : (
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Your repayment capacity satisfies the lender's FOIR policy.
              </li>

              <li>Avoid taking additional loans before disbursement.</li>

              <li>Maintain a good credit score.</li>

              <li>Compare offers from multiple lenders before sanction.</li>
            </ul>
          )}
        </InsightCard>
      </>
    );
  },
};

export default detailedFoirCalculatorConfig;
